import { Box, VStack, Skeleton } from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { BlockContainer } from "src/atoms";
import Renderer from "src/lib/renderer";
import { heading2, heading3 } from "src/lib/renderer/elements/headings";
import paragraph from "src/lib/renderer/elements/paragraph";
import { Element } from "@prismicio/react";

declare global {
  interface Window {
    MktoForms2?: {
      loadForm: (baseUrl: string, munchkinId: string, formId: string, callback?: (form: any) => void) => void;
    };
  }
}

const BlockEmbedMarketoForm: FC<
  (
    | Gatsby.PageBlockEmbedMarketoFormFragment
    | Gatsby.PageLandingBlockEmbedMarketoFormFragment
    | Gatsby.PageFragmentBlockEmbedMarketoFormFragment
  ) & { index: number }
> = ({ primary }) => {
  const [formLoaded, setFormLoaded] = useState(false);

  if (!primary) return null;

  const { content, form_id, munchkin_id, base_url } = primary;

  if (!form_id || !munchkin_id || !base_url) {
    return null;
  }

  useEffect(() => {
    if (formLoaded) return;

    const scriptSrc = `${base_url}/js/forms2/js/forms2.min.js`;

    const initForm = () => {
      if (window.MktoForms2) {
        const formEl = document.getElementById(`mktoForm_${form_id}`);
        
        const enhanceDynamicFields = (formElem: HTMLElement) => {
          formElem.querySelectorAll('.mktoField, input[type="checkbox"]').forEach((field) => {
            const row = field.closest('.mktoFormRow');
            if (row) {
              if (field.tagName.toLowerCase() === 'textarea' && !row.classList.contains('row-textarea')) {
                row.classList.add('row-textarea');
              }
              if (field.getAttribute('type') === 'checkbox' && !row.classList.contains('row-checkbox')) {
                row.classList.add('row-checkbox');
              }
            }
          });
        };

        if (!formEl?.hasChildNodes()) {
          window.MktoForms2.loadForm(base_url, munchkin_id, form_id, (form) => {
            enhanceDynamicFields(form.getFormElem()[0]);
            setFormLoaded(true);
          });
        } else {
          enhanceDynamicFields(formEl);
          setFormLoaded(true);
        }
      }
    };

    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

    if (existingScript) {
      if (window.MktoForms2) {
        initForm();
      } else {
        existingScript.addEventListener("load", initForm);
      }
    } else {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;
      script.addEventListener("load", initForm);
      document.body.appendChild(script);
    }
  }, [base_url, munchkin_id, form_id, formLoaded]);

  // Prismic often returns an empty paragraph when no content is provided
  const hasContent = !!(
    content?.raw &&
    Array.isArray(content.raw) &&
    content.raw.some((block: any) => block.text && block.text.trim().length > 0)
  );

  return (
    <BlockContainer paddingY={{ base: "space-40", md: "space-64" }}>
      <VStack
        w="full"
        spacing="space-32"
        alignItems="center"
      >
        {hasContent ? (
          <Box w="full" textAlign="center">
            <Renderer
              overrides={{
                [Element.heading2]: (args) => heading2({ ...args, overrideProps: { textAlign: "center" } }),
                [Element.heading3]: (args) => heading3({ ...args, overrideProps: { textAlign: "center" } }),
                [Element.paragraph]: (args) =>
                  paragraph({
                    ...args,
                    overrideProps: {
                      textColor: "grey-1",
                      textAlign: "center"
                    },
                  }),
              }}
              field={content}
            />
          </Box>
        ) : null}
        <Box
          w="full"
          maxWidth="960px"
          mx="auto"
          overflow="hidden"
          boxShadow="card"
          padding="space-24"
          sx={{
            ".mktoForm": {
              width: "100% !important",
              fontFamily: "inherit !important",
              display: "flex !important",
              flexWrap: "wrap !important",
              justifyContent: "space-between !important",
            },
            /* Make Marketo rows 100% by default on mobile, 49% on tablet/desktop */
            ".mktoFormRow": {
              width: "100% !important",
              marginBottom: "16px !important",
              clear: "both !important",
              "@media (min-width: 768px)": {
                width: "calc(50% - 8px) !important",
              }
            },
            
            /* Explicitly 100% width for Textarea (Comment) and Checkbox (Opt-in) */
            ".mktoFormRow.row-textarea, .mktoFormRow.row-checkbox": {
              "@media (min-width: 768px)": {
                width: "100% !important",
              }
            },

            ".mktoButtonRow": {
              width: "100% !important",
              display: "flex !important",
              justifyContent: "center !important",
              marginTop: "8px !important",
            },
            ".mktoFormCol": {
              width: "100% !important",
              marginBottom: "0 !important",
            },
            ".mktoFieldWrap": {
              width: "100% !important",
              position: "relative",
            },

            /* Hide asterisks entirely to avoid floating dots */
            ".mktoAsterix": {
              display: "none !important",
            },

            /* Safely reset labels so empty ones take 0 space, but text labels (checkbox) show up */
            ".mktoLabel": {
              display: "block !important",
              width: "auto !important",
              float: "none !important",
              minHeight: "0 !important",
              padding: "0 !important",
              margin: "0 !important",
              fontWeight: "500 !important",
              color: "var(--chakra-colors-text-primary) !important",
            },
            
            /* Checkbox specific label styling */
            "input[type='checkbox'] ~ label, .mktoLogicalField label": {
              fontWeight: "normal !important",
              fontSize: "14px !important",
              lineHeight: "1.5 !important",
              color: "var(--chakra-colors-text-secondary, #4A5568) !important",
              cursor: "pointer",
            },
            
            /* Make privacy policy links highly clickable and on-brand, globally targeting any a tag in form */
            ".mktoForm a": {
              color: "var(--chakra-colors-accounting-green-800, #0F5F35) !important",
              textDecoration: "underline !important",
              fontWeight: "600 !important",
              display: "inline-block !important",
              padding: "4px !important",
              margin: "-4px !important",
              position: "relative",
              zIndex: 10,
            },

            ".mktoField:not([type='checkbox']):not([type='radio'])": {
              width: "100% !important",
              padding: "16px 20px !important",
              border: "1px solid var(--chakra-colors-border-primary, #E2E8F0) !important",
              borderRadius: "8px !important",
              backgroundColor: "var(--chakra-colors-bg-input, #F7FAFC) !important",
              color: "var(--chakra-colors-text-primary, #1A202C) !important",
              fontSize: "16px !important",
              lineHeight: "1.5 !important",
              transition: "all 0.2s !important",
              boxSizing: "border-box !important",
            },
            ".mktoField:not([type='checkbox']):not([type='radio']):focus": {
              borderColor: "var(--chakra-colors-btn-green-bg, #0F5F35) !important",
              boxShadow: "0 0 0 1px var(--chakra-colors-btn-green-bg, #0F5F35) !important",
              outline: "none !important",
              backgroundColor: "white !important",
            },
            
            /* Increase height for the Commentaire text area */
            "textarea.mktoField": {
              minHeight: "120px !important",
              resize: "vertical !important",
            },

            ".mktoButtonWrap": {
              marginLeft: "0 !important",
            },
            ".mktoButton": {
              background: "var(--chakra-colors-accounting-green-800, #0F5F35) !important",
              color: "white !important",
              border: "none !important",
              padding: "16px 32px !important",
              borderRadius: "9999px !important",
              fontSize: "16px !important",
              fontWeight: "bold !important",
              cursor: "pointer !important",
              transition: "background-color 0.2s !important",
              width: "100% !important",
              "@media (min-width: 768px)": {
                width: "auto !important",
                minWidth: "200px !important",
              }
            },
            ".mktoButton:hover": {
              background: "var(--chakra-colors-accounting-green-950, #003725) !important",
            },
            
            /* Fix Marketo inline 10px widths on checkbox containers */
            ".mktoLogicalField, .mktoCheckboxList": {
              display: "flex !important",
              alignItems: "flex-start !important",
              gap: "12px !important",
              width: "100% !important",
              marginTop: "-6px !important",
            },
            "input[type='checkbox']": {
              minWidth: "18px !important",
              height: "18px !important",
              cursor: "pointer",
              marginTop: "0px !important", // align with text
            },

            ".mktoOffset, .mktoClear, .mktoGutter": {
              display: "none !important",
            }
          }}
        >
          {!formLoaded && (
            <VStack spacing="16px" w="full" align="stretch">
              {/* Desktop layout skeleton */}
              <Box display={{ base: "none", md: "flex" }} gap="16px">
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
              </Box>
              <Box display={{ base: "none", md: "flex" }} gap="16px">
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
              </Box>
              <Box display={{ base: "none", md: "flex" }} gap="16px">
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
                <Box w="full" /> {/* Empty spacing for the 6th spot */}
              </Box>
              
              {/* Mobile layout skeleton */}
              <Box display={{ base: "flex", md: "none" }} flexDirection="column" gap="16px">
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w="full" borderRadius="8px" />
              </Box>

              <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="120px" w="full" borderRadius="8px" />
              
              <Box display="flex" alignItems="flex-start" gap="12px">
                 <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="18px" w="18px" borderRadius="4px" flexShrink={0} />
                 <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="20px" w="80%" borderRadius="4px" />
              </Box>
              
              <Box display="flex" justifyContent="center" mt="8px">
                <Skeleton startColor="prosperity-stone-50" endColor="prosperity-stone-200" height="56px" w={{ base: "100%", md: "200px" }} borderRadius="9999px" />
              </Box>
            </VStack>
          )}

          <Box display={formLoaded ? "block" : "none"} w="full" opacity={formLoaded ? 1 : 0} transition="opacity 0.3s">
            <form id={`mktoForm_${form_id}`} style={{ width: "100%" }} />
          </Box>
        </Box>
      </VStack>
    </BlockContainer>
  );
};

export default BlockEmbedMarketoForm;
